import { useEffect } from "react";
import useReducedMotion from "./useReducedMotion";

const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const smooth = (v) => {
  const c = clamp(v);
  return c * c * (3 - 2 * c); // smoothstep easing
};

// Drives the intro's "scroll composes the mark" choreography.
//
// Rather than mutating six DOM nodes per frame (as the original did), we write
// four normalized progress values as CSS custom properties on the section; the
// stylesheet turns them into stroke-dashoffset / opacity / transform. The rAF is
// scheduled only in response to scroll/resize — no perpetual animation loop.
export default function useIntroChoreography(sectionRef) {
  const reduced = useReducedMotion();

  useEffect(() => {
    const section = sectionRef.current;
    if (!section || reduced) return undefined;

    let top = 0;
    let len = 1;
    let raf = 0;
    let scheduled = false;

    const measure = () => {
      let el = section;
      let offset = 0;
      while (el) {
        offset += el.offsetTop;
        el = el.offsetParent;
      }
      top = offset;
      len = Math.max(1, section.offsetHeight - window.innerHeight);
    };

    const apply = () => {
      scheduled = false;
      const p = clamp((window.scrollY - top) / len);
      // rings draw 0–.34, bars .26–.58, wordmark rises .60–.80, cue fades by .06
      const s = section.style;
      s.setProperty("--p-ring", smooth(p / 0.34).toFixed(4));
      s.setProperty("--p-bar", smooth((p - 0.26) / 0.32).toFixed(4));
      s.setProperty("--p-title", smooth((p - 0.6) / 0.2).toFixed(4));
      s.setProperty("--p-cue", (1 - clamp(p / 0.06)).toFixed(4));
    };

    const schedule = () => {
      if (scheduled || typeof requestAnimationFrame !== "function") return;
      scheduled = true;
      raf = requestAnimationFrame(apply);
    };

    const onResize = () => {
      measure();
      schedule();
    };

    measure();
    apply();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", onResize);

    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", onResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [sectionRef, reduced]);
}
